import { z } from 'zod'
import { readFileSync, existsSync } from 'fs'

// Type alias for cleaner code
type EnvSchema = Record<string, z.ZodTypeAny>

export interface EnvConfig<TClient extends EnvSchema, TServer extends EnvSchema> {
  client: TClient
  server: TServer
}

export interface EnvOptions {
  exampleFile?: string
  allowMissingInProduction?: boolean
  onValidationError?: (error: z.ZodError) => void
  strict?: boolean
  debug?: boolean
}

type InferClient<T extends EnvSchema> = {
  [K in keyof T]: z.infer<T[K]>
}

type InferServer<T extends EnvSchema> = {
  [K in keyof T]: z.infer<T[K]>
}

type InferEnv<TClient extends EnvSchema, TServer extends EnvSchema> = 
  InferClient<TClient> & InferServer<TServer>

/**
 * Zod 4 utility functions for common environment variable patterns
 */
export const envUtils = {
  /**
   * Creates a boolean environment variable parser with improved Zod 4 validation
   * Accepts 'true', 'false', '1', '0', 'yes', 'no' (case insensitive)
   */
  boolean: () => z.string().transform((val: string) => {
    const normalized = val.toLowerCase().trim()
    if (['true', '1', 'yes', 'on'].includes(normalized)) return true
    if (['false', '0', 'no', 'off'].includes(normalized)) return false
    throw new Error(`Invalid boolean value: ${val}. Expected 'true'/'false', '1'/'0', 'yes'/'no', or 'on'/'off'`)
  }),

  /**
   * Creates a number environment variable parser with Zod 4's improved validation
   */
  number: () => z.string().transform((val: string) => {
    const num = Number(val)
    if (isNaN(num)) {
      throw new Error(`Invalid number: ${val}`)
    }
    return num
  }),

  /**
   * Creates an integer environment variable parser
   */
  integer: () => z.string().transform((val: string) => {
    const num = parseInt(val, 10)
    if (isNaN(num)) {
      throw new Error(`Invalid integer: ${val}`)
    }
    return num
  }),

  /**
   * Creates a URL environment variable parser with Zod 4's improved URL validation
   */
  url: () => z.string().url(),

  /**
   * Creates an email environment variable parser with Zod 4's improved email validation
   */
  email: () => z.string().email(),

  /**
   * Creates a port number parser (1-65535)
   */
  port: () => z.string().transform((val: string) => {
    const port = parseInt(val, 10)
    if (isNaN(port) || port < 1 || port > 65535) {
      throw new Error(`Invalid port number: ${val}. Must be between 1 and 65535`)
    }
    return port
  }),

  /**
   * Creates a JSON environment variable parser with Zod 4's improved error handling
   */
  json: <T>(schema?: z.ZodType<T>) => z.string().transform((val: string) => {
    try {
      const parsed = JSON.parse(val)
      if (schema) {
        return schema.parse(parsed)
      }
      return parsed
    } catch (error) {
      throw new Error(`Invalid JSON: ${val}`)
    }
  }),

  /**
   * Creates a comma-separated list parser
   */
  list: (separator = ',') => z.string().transform((val: string) => 
    val.split(separator).map(s => s.trim()).filter(Boolean)
  ),

  /**
   * Creates a secret key parser with minimum length validation
   */
  secret: (minLength = 32) => z.string().min(minLength, {
    message: `Secret must be at least ${minLength} characters long`
  }),
}

/**
 * Parses .env.example file and returns a set of required variable names
 */
function parseEnvExample(exampleFilePath: string): Set<string> {
  if (!existsSync(exampleFilePath)) {
    return new Set()
  }

  try {
    const content = readFileSync(exampleFilePath, 'utf-8')
    const requiredVars = new Set<string>()

    content.split('\n').forEach(line => {
      const trimmed = line.trim()
      if (trimmed && !trimmed.startsWith('#') && !trimmed.startsWith('//')) {
        const [key] = trimmed.split('=')
        if (key && !key.includes(' ')) {
          requiredVars.add(key.trim())
        }
      }
    })

    return requiredVars
  } catch (error) {
    console.warn(`⚠️  Could not read ${exampleFilePath}:`, error)
    return new Set()
  }
}

/**
 * Validates that all required variables from .env.example are present
 */
function validateAgainstExample(
  env: Record<string, string | undefined>,
  exampleVars: Set<string>,
  allowMissingInProduction: boolean
): void {
  const missingVars: string[] = []
  
  for (const requiredVar of exampleVars) {
    if (!env[requiredVar] && !allowMissingInProduction) {
      missingVars.push(requiredVar)
    }
  }

  if (missingVars.length > 0) {
    const error = new Error(
      `❌ Missing required environment variables:\n` +
      `${missingVars.map(varName => `  - ${varName}`).join('\n')}\n\n` +
      `💡 To fix this:\n` +
      `  1. Copy .env.example to .env\n` +
      `  2. Fill in the missing values\n` +
      `  3. Or set allowMissingInProduction: true if these are optional`
    )
    throw error
  }
}

/**
 * Enhances Zod 4 error messages with more helpful context
 */
function enhanceZodError(error: z.ZodError, schemas: EnvSchema): z.ZodError {
  const enhancedIssues = error.issues.map(issue => {
    // Handle nested paths for complex validation
    const varName = Array.isArray(issue.path) ? issue.path.join('.') : 'unknown'
    
    let enhancedMessage = issue.message
    
    // Add helpful hints based on error code and message (Zod 4 has improved error messages)
    if (issue.message.includes('Invalid URL')) {
      enhancedMessage += `\n💡 Expected a valid URL (e.g., https://api.example.com)`
    } else if (issue.message.includes('too small')) {
      enhancedMessage += `\n💡 Check the minimum length requirement`
    } else if (issue.message.includes('Invalid option')) {
      enhancedMessage += `\n💡 Check the allowed values for this environment variable`
    } else if (issue.message.includes('Invalid JSON')) {
      enhancedMessage += `\n💡 Expected valid JSON format`
    } else if (issue.message.includes('unrecognized keys')) {
      enhancedMessage += `\n💡 Remove unknown environment variables or add them to your schema`
    } else if (issue.message.includes('Invalid email')) {
      enhancedMessage += `\n💡 Expected a valid email address (e.g., user@example.com)`
    } else if (issue.message.includes('Invalid boolean')) {
      enhancedMessage += `\n💡 Expected 'true'/'false', '1'/'0', 'yes'/'no', or 'on'/'off'`
    } else if (issue.message.includes('Invalid number')) {
      enhancedMessage += `\n💡 Expected a valid number`
    } else if (issue.message.includes('Invalid integer')) {
      enhancedMessage += `\n💡 Expected a valid integer`
    } else if (issue.message.includes('Invalid port')) {
      enhancedMessage += `\n💡 Expected a port number between 1 and 65535`
    }
    
    // Add enum options if available (Zod 4 has improved enum handling)
    const schema = schemas[varName]
    if (schema && schema instanceof z.ZodEnum) {
      const options = schema.options?.join(', ')
      enhancedMessage += `\n💡 Allowed values: ${options}`
    }
    
    // Add transform hints for common patterns
    if (issue.message.includes('Expected string')) {
      enhancedMessage += `\n💡 Environment variables are always strings. Use transforms to convert to other types.`
    }
    
    return {
      ...issue,
      message: enhancedMessage
    }
  })
  
  return new z.ZodError(enhancedIssues)
}

/**
 * Validates a schema and returns the parsed result or collects issues
 */
function validateSchema<T extends EnvSchema>(
  schemaObj: T,
  env: Record<string, any>,
  strict: boolean
): { success: true; data: InferClient<T> } | { success: false; error: z.ZodError } {
  // Only include keys that are actually set (not undefined)
  const filteredEnv: Record<string, any> = {}
  for (const key of Object.keys(schemaObj)) {
    if (env[key] !== undefined) {
      filteredEnv[key] = env[key]
    }
    // If not present or undefined, omit the key so Zod applies default/optional
  }
  
  const schema = z.object(schemaObj)
  const parsed = schema.safeParse(filteredEnv)
  if (parsed.success) {
    return { success: true, data: parsed.data as InferClient<T> }
  } else {
    return { success: false, error: parsed.error }
  }
}

let _envCache: any = null
let _envCacheKey: string | null = null

/**
 * Creates a type-safe environment variable validator with support for nested objects and transforms
 * @param config - Configuration object with client and server schemas
 * @param options - Optional configuration for validation behavior
 * @returns Validated environment variables with full type inference
 */
export function createEnv<
  TClient extends EnvSchema,
  TServer extends EnvSchema
>(
  config: EnvConfig<TClient, TServer>,
  options: EnvOptions = {}
): InferEnv<TClient, TServer> {
  const envKey = JSON.stringify(process.env)
  if (_envCache && _envCacheKey === envKey) {
    return _envCache
  }

  const { client, server } = config
  const { 
    exampleFile = '.env.example', 
    allowMissingInProduction = false,
    strict = false,
    debug = false,
    onValidationError = (error) => {
      console.error('❌ [envfortress] Invalid environment variables:', error.issues)
      process.exit(1)
    }
  } = options

  // Parse .env.example if it exists
  const exampleVars = parseEnvExample(exampleFile)
  
  // Warn about production enforcement
  if (
    !allowMissingInProduction &&
    process.env.NODE_ENV === 'production' &&
    exampleVars.size > 0
  ) {
    console.warn(
      '⚠️ You are enforcing .env.example validation in production. Consider setting `allowMissingInProduction: true` if this causes deploy issues.'
    )
  }
  
  // Validate against .env.example first
  if (exampleVars.size > 0) {
    validateAgainstExample(process.env, exampleVars, allowMissingInProduction)
  }

  // Warn on undefined process.env keys in development
  if (process.env.NODE_ENV !== 'production') {
    for (const key in process.env) {
      if (typeof process.env[key] === 'undefined') {
        console.warn(`⚠️ process.env["${key}"] is undefined`)
      }
    }
  }

  // Validate client and server schemas separately using helper
  const clientParsed = validateSchema(client, process.env, strict)
  const serverParsed = validateSchema(server, process.env, strict)

  // Check for overlap between client and server schemas
  const clientKeys = Object.keys(client)
  const serverKeys = Object.keys(server)
  const overlap = clientKeys.filter(key => serverKeys.includes(key))
  
  if (overlap.length > 0) {
    const error = new Error(
      `❌ Environment variable overlap detected:\n` +
      `${overlap.map(key => `  - ${key} is defined in both client and server schemas`).join('\n')}\n\n` +
      `💡 Each variable should only be defined in one schema (client or server)`
    )
    throw error
  }

  // Handle validation errors
  if (clientParsed.success === false || serverParsed.success === false) {
    const allIssues = [
      ...(clientParsed.success ? [] : clientParsed.error.issues),
      ...(serverParsed.success ? [] : serverParsed.error.issues),
    ]
    const combinedError = new z.ZodError(allIssues)
    onValidationError(enhanceZodError(combinedError, { ...client, ...server }))
    return {} as InferEnv<TClient, TServer>
  }

  // Merge client and server data
  const merged = {
    ...clientParsed.data,
    ...serverParsed.data,
  } as InferEnv<TClient, TServer>

  // Debug log if enabled
  if (debug && process.env.NODE_ENV !== 'production') {
    console.log('[envfortress] Parsed env:', merged)
  }

  _envCache = merged
  _envCacheKey = envKey
  return merged
}

/**
 * Creates a public-only environment validator (for client-side only)
 * @param schema - Schema for public environment variables
 * @param options - Optional configuration
 * @returns Validated environment variables
 */
export function createPublicEnv<TClient extends EnvSchema>(
  schema: TClient,
  options: EnvOptions = {}
): InferClient<TClient> {
  const envKey = JSON.stringify(process.env)
  if (_envCache && _envCacheKey === envKey) {
    return _envCache as InferClient<TClient>
  }
  const validated = createEnv({ client: schema, server: {} }, options) as InferClient<TClient>
  _envCache = validated
  _envCacheKey = envKey
  return validated
}

export function _clearEnvCacheForTests() {
  _envCache = null;
  _envCacheKey = null;
}

// Re-export Zod and inferred types for convenience
export { z }
export type { InferClient, InferServer, InferEnv, EnvSchema }
export default createEnv
